/**
 * OpenRouter Client
 *
 * A cross-provider fallback client that routes requests through OpenRouter.
 * Used as a last-resort fallback when all same-provider models have failed.
 *
 * All calls route through the openRouterProxy Cloud Function to keep API keys server-side.
 * Uses codename "Prism" in user-facing messages — never mention "OpenRouter".
 *
 * Model usage:
 *   - shouldTagAsTest() → true: uses OPENROUTER_API_KEY_TEST ("Prest0 testing" key)
 *   - shouldTagAsTest() → false: uses OPENROUTER_API_KEY_USER ("Prest0 user usage" key)
 *
 * 3-tier key architecture:
 *   - OPENROUTER_API_KEY_USER: production user-facing agentic workflows
 *   - OPENROUTER_API_KEY_TEST: testing/dev/preview environments
 *   - "Prest0n" key: internal Slack/agent usage (NOT managed in this codebase)
 */

import type { TokenUsageMetadata } from '../components/GlobalChatSystem/types';
import { shouldTagAsTest } from '../config/agenticWorkflowConfig';

// Returns the current account ID for log attribution.
// Reads from localStorage first (set by auth flow), falls back to URL path segment.
function getAccountHint(): string {
  try {
    const raw = localStorage.getItem('user_accounts');
    if (raw) {
      const accounts = JSON.parse(raw);
      if (Array.isArray(accounts) && accounts.length > 0 && accounts[0].accountId) {
        return accounts[0].accountId;
      }
    }
    const pathPart = window.location.pathname.split('/')[1];
    return pathPart || 'unknown';
  } catch {
    return 'unknown';
  }
}

// OpenRouter proxy URL — routes through Firebase Cloud Function
function getOpenRouterProxyUrl(): string {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/openRouterProxy`;
}

// anthropicProxy URL — the task-mode door (D14 slice 5b-ii). Same override
// convention as the other anthropicProxy clients.
function getAnthropicProxyUrl(): string {
  const override = (process.env.REACT_APP_ANTHROPIC_PROXY_URL || '').trim();
  if (override) return override;
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/anthropicProxy`;
}

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;

// Helper to delay for exponential backoff
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export interface OpenRouterMessage {
  role: 'system' | 'user' | 'assistant';
  content: string | OpenRouterContentBlock[];
}

export type OpenRouterContentBlock =
  | { type: 'text'; text: string }
  | { type: 'image_url'; image_url: { url: string } }
  | { type: 'file_url'; file_url: { url: string; mime_type?: string; filename?: string } };

export interface OpenRouterAttachment {
  type: 'image_url' | 'file_url';
  url: string;
  mimeType?: string;
  filename?: string;
}

export interface OpenRouterParams {
  /** Model ID (e.g., 'google/gemini-3.1-pro-preview', 'openai/gpt-5.4') */
  model: string;
  /** Messages to send */
  messages: OpenRouterMessage[];
  /** Max tokens for response */
  maxTokens?: number;
  /** Temperature (0.0-1.0) */
  temperature?: number;
  /** Optional abort signal for cancellation */
  signal?: AbortSignal;
  /** Reasoning effort level (for models that support it). 'xhigh' added
   *  2026-08-15: the grok-4.6 rung of the tier-14 deep-research ladder pins
   *  thinking:xhigh in the SSOT (4.6 widened the xAI enum; 4.5-and-earlier
   *  treat xhigh AS high, so this can never 400 on an alias re-point). */
  reasoning?: { effort: 'low' | 'medium' | 'high' | 'xhigh' };
  /** OpenRouter plugins (e.g., [{ id: 'web' }] for web search grounding) */
  plugins?: Array<{ id: string }>;
  /** Optional multimodal attachments added to user messages */
  attachments?: OpenRouterAttachment[];
}

export interface OpenRouterStreamParams extends OpenRouterParams {
  /** Called for each text chunk received from the stream */
  onChunk?: (chunk: string, accumulated: string) => void;
  /** Called when streaming is complete */
  onComplete?: (fullText: string, tokenUsage?: TokenUsageMetadata) => void;
  /** Called on error */
  onError?: (error: Error) => void;
  /**
   * Called on EVERY read that returns bytes — content deltas, reasoning
   * events, and OpenRouter's keep-alive comments alike (2026-08-15).
   *
   * WHY THIS EXISTS SEPARATELY FROM onChunk. onChunk fires only when
   * `choices[0].delta.content` is non-empty. With `reasoning: {effort:'high'}`
   * the model can spend MINUTES emitting reasoning tokens before its first
   * content token, and keep-alive comments do not even start with "data: " so
   * they are skipped outright. Every liveness signal this client exposed
   * therefore required CONTENT, and went silent during the single longest
   * phase of a large segment.
   *
   * That blind spot killed Fernando's 200-page manual three times: the
   * orchestrator's inactivity ceiling saw no callbacks, concluded the turn was
   * dead, and aborted a run whose socket was demonstrably alive — the 60s
   * chunk timeout never tripped once.
   *
   * Bytes on the wire are the honest liveness signal. A dead connection still
   * dies on CHUNK_TIMEOUT_MS, so this cannot mask a genuine hang.
   */
  onActivity?: () => void;
}

export interface OpenRouterResult {
  text: string;
  raw?: unknown;
  tokenUsage?: TokenUsageMetadata;
}

function withAttachments(
  messages: OpenRouterMessage[],
  attachments?: OpenRouterAttachment[]
): OpenRouterMessage[] {
  if (!attachments || attachments.length === 0) return messages;

  const attachmentBlocks: OpenRouterContentBlock[] = attachments.map((attachment) => {
    if (attachment.type === 'image_url') {
      return {
        type: 'image_url',
        image_url: { url: attachment.url },
      };
    }

    return {
      type: 'file_url',
      file_url: {
        url: attachment.url,
        ...(attachment.mimeType ? { mime_type: attachment.mimeType } : {}),
        ...(attachment.filename ? { filename: attachment.filename } : {}),
      },
    };
  });

  return messages.map((message) => {
    if (message.role !== 'user') return message;
    const baseBlocks: OpenRouterContentBlock[] = typeof message.content === 'string'
      ? [{ type: 'text', text: message.content }]
      : [...message.content];
    return {
      ...message,
      content: [...baseBlocks, ...attachmentBlocks],
    };
  });
}

/**
 * Check if an error is an auth error (non-retryable)
 */
function isAuthError(err: any): boolean {
  const AUTH_CODES = ['API_KEY_NOT_CONFIGURED', 'OPENROUTER_AUTH_FAILED'];
  return AUTH_CODES.includes(err?.code) || err?.status === 401;
}

/**
 * Check if an error is retryable (transient)
 */
function isRetryable(err: any): boolean {
  if (isAuthError(err)) return false;
  const status = err?.status;
  return status === 500 || status === 502 || status === 503 || status === 504 ||
    status === 429 || status === 408;
}

/**
 * Execute a non-streaming OpenRouter request.
 * Routes through the openRouterProxy Cloud Function.
 */
export async function runOpenRouterRequest(params: OpenRouterParams): Promise<OpenRouterResult> {
  const proxyUrl = getOpenRouterProxyUrl();
  const startTime = Date.now();

  const body = {
    model: params.model,
    messages: withAttachments(params.messages, params.attachments),
    stream: false,
    isTestMode: shouldTagAsTest(),
    ...(params.maxTokens ? { max_tokens: params.maxTokens } : {}),
    ...(params.temperature !== undefined ? { temperature: params.temperature } : {}),
    ...(params.reasoning ? { reasoning: params.reasoning } : {}),
    ...(params.plugins ? { plugins: params.plugins } : {}),
  };

  let lastError: any = null;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      const backoffMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
      console.log(`[OpenRouter] Retry ${attempt}/${MAX_RETRIES} after ${backoffMs}ms for ${params.model}`);
      await delay(backoffMs);
    }

    try {
      const accountHint = getAccountHint();
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Account-Id': accountHint,
        },
        body: JSON.stringify(body),
        signal: params.signal,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message = (data?.error as any)?.message || data?.message || `Prism request failed (${response.status})`;
        const code = (data?.error as any)?.code || data?.code || `HTTP_${response.status}`;
        const err: any = new Error(message);
        err.status = response.status;
        err.code = code;
        lastError = err;

        if (!isRetryable(err) || attempt === MAX_RETRIES - 1) throw err;
        continue;
      }

      const data = await response.json();
      const text = data?.choices?.[0]?.message?.content?.trim() || '';
      if (!text) {
        const err: any = new Error('Prism returned an empty response.');
        err.status = 200;
        lastError = err;
        if (attempt === MAX_RETRIES - 1) throw err;
        continue;
      }

      const responseTimeMs = Date.now() - startTime;
      const usage = data?.usage;
      const tokenUsage: TokenUsageMetadata = {
        inputTokens: usage?.prompt_tokens,
        outputTokens: usage?.completion_tokens,
        totalTokens: usage?.total_tokens,
        model: params.model,
        provider: 'openrouter',
        responseTimeMs,
        timestamp: new Date(),
      };

      console.log(`[OpenRouter] Non-streaming request completed for ${params.model} in ${responseTimeMs}ms`);
      return { text, raw: data, tokenUsage };

    } catch (fetchErr: any) {
      lastError = fetchErr;
      if (!isRetryable(fetchErr) || attempt === MAX_RETRIES - 1) throw fetchErr;
    }
  }

  throw lastError || new Error('Prism request failed after retries');
}

/**
 * Execute a streaming OpenRouter request.
 * Routes through the openRouterProxy Cloud Function.
 * Parses OpenAI-compatible SSE streaming format.
 */
export async function runOpenRouterStream(params: OpenRouterStreamParams): Promise<OpenRouterResult> {
  const body = {
    model: params.model,
    messages: withAttachments(params.messages, params.attachments),
    stream: true,
    isTestMode: shouldTagAsTest(),
    ...(params.maxTokens ? { max_tokens: params.maxTokens } : {}),
    ...(params.temperature !== undefined ? { temperature: params.temperature } : {}),
    ...(params.reasoning ? { reasoning: params.reasoning } : {}),
    ...(params.plugins ? { plugins: params.plugins } : {}),
  };

  return streamThroughProxy({
    proxyUrl: getOpenRouterProxyUrl(),
    body,
    displayModel: params.model,
    signal: params.signal,
    onChunk: params.onChunk,
    onComplete: params.onComplete,
    onError: params.onError,
    onActivity: params.onActivity,
  });
}

/**
 * D14 slice 5b-ii: the task-mode streaming transport — the route name (and,
 * for ladder descent, a rung offset) is the ONLY routing input; the server
 * stamps the SSOT model/ladder/effort (taskModeRewrite) and its stream is the
 * same OpenRouter chunk format this client already parses (Door A is a
 * passthrough — probe-verified 2026-08-26). A rung offset past the ladder end
 * answers 400 TASK_LADDER_EXHAUSTED, which walking callers treat as the end
 * of descent, never as an error to retry.
 */
export interface TaskStreamParams {
  /** SSOT task route name (e.g. 'translation'). */
  route: string;
  /** Ladder rung offset for quality-judgment descent (default 0). */
  rung?: number;
  messages: OpenRouterMessage[];
  maxTokens?: number;
  temperature?: number;
  signal?: AbortSignal;
  /** Multimodal attachments appended to user messages (OpenRouter-form
   *  blocks — Door A's converter passes non-Anthropic blocks through). */
  attachments?: OpenRouterAttachment[];
  onChunk?: (chunk: string, accumulated: string) => void;
  onComplete?: (fullText: string, tokenUsage?: TokenUsageMetadata) => void;
  onError?: (error: Error) => void;
  onActivity?: () => void;
}

export async function runTaskStream(params: TaskStreamParams): Promise<OpenRouterResult> {
  const rung = params.rung && params.rung > 0 ? params.rung : 0;
  const body = {
    mode: 'task',
    route: params.route,
    ...(rung > 0 ? { rung } : {}),
    messages: withAttachments(params.messages, params.attachments),
    stream: true,
    stream_options: { include_usage: true },
    ...(params.maxTokens ? { max_tokens: params.maxTokens } : {}),
    ...(params.temperature !== undefined ? { temperature: params.temperature } : {}),
  };

  return streamThroughProxy({
    proxyUrl: getAnthropicProxyUrl(),
    body,
    extraHeaders: { 'X-Prest0n-Task': params.route },
    displayModel: `task:${params.route}${rung > 0 ? `#${rung}` : ''}`,
    signal: params.signal,
    onChunk: params.onChunk,
    onComplete: params.onComplete,
    onError: params.onError,
    onActivity: params.onActivity,
  });
}

/**
 * Shared connect-retry + SSE parse core for both proxy doors. The stream is
 * OpenAI-chunk-shaped on both (Door A passes OpenRouter chunks through).
 * tokenUsage.model prefers the SERVED id the chunks carry (R17-F doctrine:
 * served-model truth beats the requested label) and falls back to the
 * display label.
 */
async function streamThroughProxy(args: {
  proxyUrl: string;
  body: Record<string, unknown>;
  extraHeaders?: Record<string, string>;
  displayModel: string;
  signal?: AbortSignal;
  onChunk?: (chunk: string, accumulated: string) => void;
  onComplete?: (fullText: string, tokenUsage?: TokenUsageMetadata) => void;
  onError?: (error: Error) => void;
  onActivity?: () => void;
}): Promise<OpenRouterResult> {
  const { proxyUrl, body } = args;
  const params = args;
  const startTime = Date.now();

  let response: Response | null = null;
  let lastError: any = null;

  // Retry connection
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      const backoffMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
      console.log(`[OpenRouter Stream] Retry ${attempt}/${MAX_RETRIES} after ${backoffMs}ms for ${args.displayModel}`);
      await delay(backoffMs);
    }

    try {
      const accountHint = getAccountHint();
      response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'X-Account-Id': accountHint,
          ...(args.extraHeaders || {}),
        },
        body: JSON.stringify(body),
        signal: params.signal,
      });

      if (response.ok || !isRetryable({ status: response.status })) break;

      if (attempt < MAX_RETRIES - 1) continue;
    } catch (fetchErr: any) {
      lastError = fetchErr;
      if (attempt === MAX_RETRIES - 1) {
        params.onError?.(fetchErr);
        throw fetchErr;
      }
    }
  }

  if (!response) {
    const err = lastError || new Error('Failed to connect to Prism after retries');
    params.onError?.(err);
    throw err;
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const message = (data?.error as any)?.message || data?.message || `Prism request failed (${response.status})`;
    const code = (data?.error as any)?.code || data?.code || `HTTP_${response.status}`;
    const err: any = new Error(message);
    err.status = response.status;
    err.code = code;
    params.onError?.(err);
    throw err;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    const err = new Error('No response body for Prism streaming');
    params.onError?.(err);
    throw err;
  }

  const decoder = new TextDecoder('utf-8');
  let accumulated = '';
  let buffer = '';
  let lastUsage: any = null;
  let lastFinishReason: string | null = null;
  let lastServedModel: string | null = null;

  // Per-chunk timeout — 30s is sufficient for OpenRouter (faster than Perplexity)
  const CHUNK_TIMEOUT_MS = 60000;

  try {
    while (true) {
      if (params.signal?.aborted) {
        console.log('[OpenRouter Stream] Abort signal detected - stopping stream');
        break;
      }

      const readPromise = reader.read();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Prism stream chunk timeout after 60 seconds')), CHUNK_TIMEOUT_MS);
      });

      let readResult: ReadableStreamReadResult<Uint8Array>;
      try {
        readResult = await Promise.race([readPromise, timeoutPromise]);
      } catch (timeoutError) {
        console.error('[OpenRouter Stream] Stream chunk timeout');
        throw timeoutError;
      }

      const { done, value } = readResult;
      if (done) break;

      // Bytes arrived. This is the ONLY liveness signal that stays true during
      // high-effort reasoning and keep-alive traffic — see onActivity.
      params.onActivity?.();

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed === 'data: [DONE]') continue;
        if (!trimmed.startsWith('data: ')) continue;

        try {
          const jsonStr = trimmed.slice(6);
          const parsed = JSON.parse(jsonStr);

          // OpenAI-compatible delta
          const delta = parsed?.choices?.[0]?.delta?.content;
          if (delta) {
            accumulated += delta;
            params.onChunk?.(delta, accumulated);
          }

          const finishReason = parsed?.choices?.[0]?.finish_reason;
          if (finishReason) lastFinishReason = finishReason;

          if (parsed?.usage) lastUsage = parsed.usage;
          if (parsed?.model) lastServedModel = String(parsed.model);
        } catch {
          // Ignore malformed JSON chunks
        }
      }
    }

    // Process remaining buffer
    if (buffer.trim() && buffer.trim().startsWith('data: ') && buffer.trim() !== 'data: [DONE]') {
      try {
        const jsonStr = buffer.trim().slice(6);
        const parsed = JSON.parse(jsonStr);
        const delta = parsed?.choices?.[0]?.delta?.content;
        if (delta) {
          accumulated += delta;
          params.onChunk?.(delta, accumulated);
        }
        if (parsed?.usage) lastUsage = parsed.usage;
        if (parsed?.choices?.[0]?.finish_reason) lastFinishReason = parsed.choices[0].finish_reason;
      } catch {
        // Ignore
      }
    }
  } finally {
    reader.releaseLock();
  }

  const responseTimeMs = Date.now() - startTime;
  const finalText = accumulated.trim();

  const tokenUsage: TokenUsageMetadata = {
    inputTokens: lastUsage?.prompt_tokens,
    outputTokens: lastUsage?.completion_tokens,
    totalTokens: lastUsage?.total_tokens,
    model: lastServedModel || args.displayModel,
    provider: 'openrouter',
    responseTimeMs,
    timestamp: new Date(),
    finishReason: lastFinishReason || undefined,
  };

  console.log(`[OpenRouter Stream] Completed ${args.displayModel} in ${responseTimeMs}ms, ${finalText.length} chars (served: ${lastServedModel || 'n/a'})`);
  params.onComplete?.(finalText, tokenUsage);

  return { text: finalText, tokenUsage };
}
